import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import MKBox from 'components/MaterialKit/MKBox';
import { handleErrorResponse } from 'utils/general';
import { withLoading } from 'utils/hoc';
import { useAuth } from 'contexts/auth';
import { getLocaleMap } from 'utils/locales';
import { splitPhoneNumber } from 'utils/phone';
import { createContact, getContact, updateContact } from 'api/monitors';
import { createContactCountry, deleteContactCountry, getCountries } from 'api/countries';
import EditContactForm from './EditContactForm';
import phoneOptionsTemplate from 'components/Select/phone_options.json';

const EditContactSection = ({
  setLoading,
  title_color,
  title_font_size,
  title_font_weight,
  edit_button_color,
  edit_font_color,
  save_button_color,
  save_button_font_color,
  back_button_color,
  back_button_font_color,
  country_options,
}) => {
  const [lm, setLm] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    getLocaleMap(['labels_edit', 'labels_create'])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const [contact, setContact] = useState(null);
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuth();
  const contactId = searchParams.get('contact_id');

  const [countries, setCountries] = useState([]);
  const fetchCountries = useCallback(() => {
    return getCountries()
      .then(({ data }) => {
        setCountries(data);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      });
  }, [setAuth, setCountries]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const fetchContactFromApi = useCallback(() => {
    if (!contactId) {
      return Promise.resolve();
    }
    setLoading(true);
    return getContact(contactId, { $expand: 'contact_countries/country' })
      .then(({ data }) => {
        setContact(data);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [contactId, setAuth, setLoading]);

  const updateContactToApi = useCallback((values, { setFieldError }) => {
    if (!contactId) {
      return Promise.resolve();
    }
    const phone = values.phone;
    const updateBody = {
      author: values.author,
      company_name: values.company_name,
      is_blacklisted: values.is_blacklisted,
      account: values.account,
      phone: phone,
    };
    const [phoneCountry, phoneRemaining] = splitPhoneNumber(phone);
    if (phoneCountry) {
      for (const phoneOption of phoneOptionsTemplate) {
        if (phoneCountry === phoneOption.value) {
          updateBody.phone_country = phoneOption.label;
          break;
        }
      }
    }

    setLoading(true);

    const indexOfAll = values.selected_countries.indexOf('all');
    if (indexOfAll > -1) { // only splice array when item is found
      values.selected_countries.splice(indexOfAll, 1);
    }

    const contactCountries = values.contact_countries;
    const prevCountryIds = values.initial_countries;
    const selectedCountryIds = values.selected_countries;
    const newContactCountryIds = selectedCountryIds.filter((selectedId) => !prevCountryIds.includes(selectedId));
    const deleteContactCountryIds = [];
    prevCountryIds.forEach((prevId) => {
      if (!selectedCountryIds.includes(prevId)) {
        const targetContactCountry = contactCountries.find((contactCountry) => contactCountry.country.country_id === prevId);
        deleteContactCountryIds.push(targetContactCountry.contact_country_id);
      }
    });
    const contactCountryPromises = [];
    newContactCountryIds.forEach((newId) => {
      const postBody = {
        contact: contactId,
        country: newId,
      };
      contactCountryPromises.push(createContactCountry(postBody));
    });
    deleteContactCountryIds.forEach((deleteId) => {
      contactCountryPromises.push(deleteContactCountry(deleteId));
    });
    return Promise.all(contactCountryPromises)
      .then(() => {
        return updateContact(contactId, updateBody)
      })
      .then(({ data }) => {
        setContact(data);
        navigate(-1);
      })
      .catch((err) => {
        setFieldError('form', err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [contactId, navigate, setLoading]);

  const createContactToApi = useCallback((values, { setFieldError }) => {
    const indexOfAll = values.selected_countries.indexOf('all');
    if (indexOfAll > -1) { // only splice array when item is found
      values.selected_countries.splice(indexOfAll, 1);
    }

    const phone = values.phone;
    const createBody = {
      author: values.author,
      company_name: values.company_name,
      is_blacklisted: values.is_blacklisted,
      account: values.account,
      phone: phone,
      contact_countries: values.selected_countries.map((selectedCountryId) => {
        return {
          country: selectedCountryId,
        };
      }),
    };
    const strippedPhone = phone ? phone.replace(/[^0-9]/g, '') : '';
    if (strippedPhone) {
      for (const phoneOption of phoneOptionsTemplate) {
        const value = phoneOption.value;
        const strippedValue = value.replace(/[^0-9]/g, '');
        if (strippedPhone.startsWith(strippedValue)) {
          createBody.phone_country = phoneOption.label;
        }
      }
    }

    setLoading(true);
    return createContact(createBody)
      .then(({ data }) => {
        setContact(data);
        navigate(-1);
      })
      .catch((err) => {
        setFieldError('form', err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate, setLoading]);

  useEffect(() => {
    fetchContactFromApi();
  }, [fetchContactFromApi]);

  return (
    <MKBox flex={1} py="2rem">
      <Container>
        <div style={{ marginBottom: 10, color: title_color, fontSize: title_font_size, fontWeight: title_font_weight }}>
          {contactId ? `Edit Contact [${contact?.author}]` : 'Create Contact'}
        </div>
        <EditContactForm
          contact={contact}
          onSave={contactId ? updateContactToApi : createContactToApi}
          countries={countries}
          editButtonColor={edit_button_color}
          editFontColor={edit_font_color}
          saveButtonColor={save_button_color}
          saveButtonFontColor={save_button_font_color}
          backButtonColor={back_button_color}
          backButtonFontColor={back_button_font_color}
          countryOptions={country_options}
        />
      </Container>
    </MKBox>
  );
};

EditContactSection.propTypes = {
  setLoading: PropTypes.func,
  title_color: PropTypes.string,
  title_font_size: PropTypes.string,
  title_font_weight: PropTypes.string,
  edit_button_color: PropTypes.string,
  edit_font_color: PropTypes.string,
  save_button_color: PropTypes.string,
  save_button_font_color: PropTypes.string,
  back_button_color: PropTypes.string,
  back_button_font_color: PropTypes.string,
  country_options: PropTypes.array,
};

export default withLoading(EditContactSection);
